import React, { useEffect, useRef } from 'react';

// CSS reference pixel convention (96px = 1in) — the same on-screen unit basis
// the original Magento plan's ruler/unit-conversion table used (`in: {px:96}`),
// distinct from the backend's print-output DPI (300) which only governs final
// export resolution, not how a ruler reads on screen.
const PX_PER_MM = 96 / 25.4;
const THICKNESS = 20;

interface RulerProps {
  lengthPx: number;  // already zoom-scaled on-screen length
  zoom: number;
  orientation: 'horizontal' | 'vertical';
}

export function Ruler({ lengthPx, zoom, orientation }: RulerProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const isH = orientation === 'horizontal';

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || lengthPx <= 0) return;
    const dpr = window.devicePixelRatio || 1;
    canvas.width  = Math.round((isH ? lengthPx : THICKNESS) * dpr);
    canvas.height = Math.round((isH ? THICKNESS : lengthPx) * dpr);
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.scale(dpr, dpr);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = '#f8fafc';
    ctx.fillRect(0, 0, isH ? lengthPx : THICKNESS, isH ? THICKNESS : lengthPx);
    ctx.strokeStyle = '#94a3b8';
    ctx.fillStyle = '#475569';
    ctx.font = '9px sans-serif';
    ctx.lineWidth = 1;

    const pxPerMmOnScreen = PX_PER_MM * zoom;
    const mmTotal = lengthPx / pxPerMmOnScreen;
    // Minor ticks every 1mm, major (labelled) ticks every 10mm — skip minor
    // ticks once they'd render closer together than ~3px apart at the
    // current zoom, since they'd otherwise just smear into a solid line.
    const minorStep = pxPerMmOnScreen >= 3 ? 1 : pxPerMmOnScreen >= 1.5 ? 5 : 10;

    for (let mm = 0; mm * pxPerMmOnScreen <= lengthPx; mm += minorStep) {
      const pos = mm * pxPerMmOnScreen;
      const isMajor = mm % 10 === 0;
      const tickLen = isMajor ? THICKNESS * 0.55 : THICKNESS * 0.3;
      ctx.beginPath();
      if (isH) {
        ctx.moveTo(pos, THICKNESS - tickLen);
        ctx.lineTo(pos, THICKNESS);
      } else {
        ctx.moveTo(THICKNESS - tickLen, pos);
        ctx.lineTo(THICKNESS, pos);
      }
      ctx.stroke();

      if (isMajor && mm > 0) {
        const label = String(mm);
        if (isH) {
          ctx.fillText(label, pos + 2, THICKNESS - tickLen - 1);
        } else {
          ctx.save();
          ctx.translate(2, pos - tickLen - 1);
          ctx.rotate(-Math.PI / 2);
          ctx.fillText(label, 0, 0);
          ctx.restore();
        }
      }
    }
    void mmTotal;
  }, [lengthPx, zoom, isH]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        width:  isH ? lengthPx : THICKNESS,
        height: isH ? THICKNESS : lengthPx,
        display: 'block',
        flexShrink: 0,
      }}
    />
  );
}

export const RULER_THICKNESS = THICKNESS;
